import {CSSProperties, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {decodeToken} from 'react-jwt'
import {useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import {HistoryContext} from '../../contexts/history'
import {caronaApi} from '../../services/CaronaApi.service'
import {CookiesService} from '../../services/Cookie.service'
import BuscaForm, {BuscaFormValues} from './components/BuscaForm'
import ResultadosList from './components/ResultadosList'
import DetalheAta, {SelecaoItens} from './components/DetalheAta'
import GerarJustificativa from './components/GerarJustificativa'
import JustificativaView from './components/JustificativaView'
import HistoricoView from './components/HistoricoView'
import DicasLoading from './components/DicasLoading'
import {COLORS, esferaDoOrgao} from './ui'
import {
    AtaDetalhe,
    AtaResumo,
    CriarJustificativaRequest,
    DocumentoProcessoOut,
    EsferaCodigo,
    EvidenciaIn,
    IrpResumo,
    JustificativaOut,
    JustificativaResumo,
    PesquisaVinculadaOut,
    RascunhoRequest,
    RespostasEntrevista,
} from './types'

// Autosave do rascunho: espera o usuário parar de digitar/marcar antes de gravar.
const RASCUNHO_AUTOSAVE_MS = 1800

type View = 'busca' | 'resultados' | 'detalhe' | 'gerar' | 'justificativa' | 'historico'

// Poll LEVE da pesquisa de preços vinculada em andamento: só enquanto a tela da
// justificativa está aberta nesse estado (spinner do card) — para assim que a
// pesquisa conclui, a tela muda ou o componente desmonta.
const PESQUISA_VINCULADA_POLL_MS = 5000

// Redação pela IA (assíncrona): o resultado chega por PUSH (notifier). Fallback
// local SEM loop de polling (regra do CTO): 1º refetch com MARGEM além do timeout
// do backend — o backend pode levar até JUSTIFICATIVA_IA_TIMEOUT=120s DEPOIS do
// 202 para persistir o desfecho, então um refetch no exato timeout leria
// PROCESSANDO e morreria (spinner eterno com o push perdido) — e no máximo MAIS
// UM refetch final. Depois disso, estado honesto com botão Atualizar (manual).
// Teto do "processando" das sugestões de termo: sem ele, um push que não chega
// (socket fora, n8n parado) deixaria o spinner eterno em cima do 0 resultados.
// Estourado o teto, cai no vazio honesto — sem sugestão, como antes.
const SUGESTOES_TERMOS_TETO_MS = 45000

const IA_TEXTO_RECONCILIACAO_MS = 150000        // 120s do backend + 30s de margem
const IA_TEXTO_RECONCILIACAO_FINAL_MS = 120000  // único refetch final adicional

/** Termo ENXUTO para pré-preencher a Pesquisa de Preços: a descrição do item
 * principal da ata (o que será precificado) ou, na falta, o termo buscado /
 * objeto da ata — nunca texto longo. */
export function termoPesquisa(
    ata: AtaDetalhe | null,
    respostas: RespostasEntrevista,
    termoBuscado: string,
    max = 140
): string {
    const itens = ata?.itens || []
    const pretendido = itens.find(
        (it) => (respostas.quantidades_desejadas[String(it.numero_item)] || 0) > 0
    )
    const bruto = (
        pretendido?.descricao || itens[0]?.descricao || termoBuscado || ata?.objeto || ''
    ).replace(/\s+/g, ' ').trim()
    if (bruto.length <= max) return bruto
    const corte = bruto.slice(0, max)
    const espaco = corte.lastIndexOf(' ')
    return (espaco > max * 0.6 ? corte.slice(0, espaco) : corte).trim()
}

function defaultRespostas(esfera: EsferaCodigo): RespostasEntrevista {
    return {
        esfera_aderente: esfera,
        demanda_descricao: '',
        quantidades_desejadas: {},
        alega_urgencia: false,
        declara_preco_compativel: false,
        declara_edital_preve_adesao: false,
        ciente_anuencias: false,
        ciente_edital: false,
        alternativas_consideradas: [],
        alternativas_observacao: '',
    }
}

/** Adaptador determinístico do mapa de evidências do front (requisito_id →
 * referência) para a LISTA que o backend espera. Descarta refs vazias; sem `tipo`
 * (o backend assume 'documento') — mesma forma já enviada ao gerar a justificativa. */
export function evidenciasToBackend(evidencias: Record<string, string>): EvidenciaIn[] {
    return Object.entries(evidencias || {})
        .filter(([, ref]) => (ref || '').trim())
        .map(([requisito_id, ref]) => ({requisito_id, ref: ref.trim()}))
}

/** Adaptador inverso: a lista de evidências do backend de volta ao mapa do front. */
export function evidenciasFromBackend(evidencias: EvidenciaIn[]): Record<string, string> {
    return Object.fromEntries(
        (evidencias || [])
            .filter((ev) => ev?.requisito_id)
            .map((ev) => [ev.requisito_id, ev.ref || ''])
    )
}

/** Há conteúdo REAL do fluxo (quantidades, evidências, respostas herdadas de um
 * rascunho antigo) para justificar salvar um rascunho? Evita criar rascunhos
 * vazios à toa — só abrir a tela ou trocar a esfera não conta. */
export function entrevistaTemConteudo(
    r: RespostasEntrevista,
    ev: Record<string, string>
): boolean {
    if ((r.demanda_descricao || '').trim()) return true
    if ((r.alternativas_observacao || '').trim()) return true
    if (Object.values(r.quantidades_desejadas || {}).some((v) => (v || 0) > 0)) return true
    if (r.demanda_prevista_pca || r.alega_urgencia || r.declara_preco_compativel
        || r.declara_edital_preve_adesao || r.ciente_anuencias || r.ciente_edital) return true
    if ((r.alternativas_consideradas || []).some(
        (a) => (a?.referencia || '').trim() || (a?.motivo || '').trim())) return true
    if (Object.values(ev || {}).some((v) => (v || '').trim())) return true
    return false
}

export default function CaronaManager() {
    // Navegação com PILHA (histórico real), em estado atômico {view, pilha}: "Voltar"
    // desempilha p/ a tela ANTERIOR de verdade — não para uma fixa. (Antes a entrevista
    // sempre voltava p/ o detalhe, mesmo vindo da lista de resultados.)
    const navigate = useNavigate()
    const [nav, setNav] = useState<{view: View; pilha: View[]}>({view: 'busca', pilha: []})
    const view = nav.view
    const navegar = (v: View) => setNav((n) => ({view: v, pilha: [...n.pilha, n.view]}))
    const voltar = () => setNav((n) => n.pilha.length
        ? {view: n.pilha[n.pilha.length - 1], pilha: n.pilha.slice(0, -1)}
        : {view: 'busca', pilha: []})
    const irParaBusca = () => setNav({view: 'busca', pilha: []})

    const [resultados, setResultados] = useState<AtaResumo[]>([])
    const [origem, setOrigem] = useState<string>('cache')
    const [termo, setTermo] = useState('')
    const [buscando, setBuscando] = useState(false)
    const [erroBusca, setErroBusca] = useState<string | null>(null)
    const [sugestoes, setSugestoes] = useState<string[]>([])
    const [irps, setIrps] = useState<IrpResumo[]>([])
    const [ultimaBusca, setUltimaBusca] = useState<BuscaFormValues | null>(null)

    const [historico, setHistorico] = useState<JustificativaResumo[]>([])
    const [carregandoHist, setCarregandoHist] = useState(false)

    const [ata, setAta] = useState<AtaDetalhe | null>(null)
    const [resumoSel, setResumoSel] = useState<AtaResumo | null>(null)
    const [carregandoAta, setCarregandoAta] = useState(false)

    // Nome do órgão aderente (cabeçalho do documento) — vem do JWT do core. A
    // ESFERA é INFERIDA do nome (fluxo automático: nada de perguntar; o seletor do
    // painel de geração deixa corrigir em um clique). Sem classificação → 'M'
    // (mesmo default do fluxo antigo, agora visível na tela).
    const aderenteNome = useMemo(() => {
        try {
            const auth = new CookiesService().getAuthData()
            const dec: any = auth?.access_token ? decodeToken(auth.access_token) : null
            return dec?.organization?.name || 'Órgão aderente'
        } catch {
            return 'Órgão aderente'
        }
    }, [])
    const esferaInferida = useMemo<EsferaCodigo>(
        () => esferaDoOrgao(aderenteNome) ?? 'M',
        [aderenteNome]
    )

    const [respostas, setRespostas] = useState<RespostasEntrevista>(() => defaultRespostas(esferaInferida))
    const [evidencias, setEvidencias] = useState<Record<string, string>>({})

    const [justificativa, setJustificativa] = useState<JustificativaOut | null>(null)
    const [gerando, setGerando] = useState(false)
    // Registro aberto do HISTÓRICO (vs. geração nova do fluxo da entrevista):
    // regra de produto — reaberto com redação nunca rodada NÃO auto-dispara a IA.
    const [reaberta, setReaberta] = useState(false)
    // O registro reaberto tem os dados da entrevista (respostas persistidas)?
    // Sem eles, "Voltar à entrevista" fica desabilitado com tooltip honesto.
    const [entrevistaDisponivel, setEntrevistaDisponivel] = useState(true)
    // A espera pela redação esgotou os (no máx. 2) refetches de reconciliação —
    // estado honesto na tela, com Atualizar manual. NUNCA loop de polling.
    const [iaEsperaEsgotada, setIaEsperaEsgotada] = useState(false)

    // Documento de planejamento (ETP/DFD/TR) — o contexto principal da redação no
    // fluxo automático (opcional: sem ele a IA parte do item pesquisado + ata).
    const [documento, setDocumento] = useState<DocumentoProcessoOut | null>(null)
    const [documentosDisponiveis, setDocumentosDisponiveis] = useState<DocumentoProcessoOut[]>([])
    const [enviandoDoc, setEnviandoDoc] = useState(false)
    const [erroDoc, setErroDoc] = useState<string | null>(null)

    // VÍNCULO PROFUNDO com a Pesquisa de Preços no fluxo PRÉ-geração: o handoff
    // liga a pesquisa ao RASCUNHO — sem justificativa carregada, o painel VIGIA o
    // registro do vínculo (GET leve) para o card aparecer aqui e o Gerar esperar a
    // conclusão da pesquisa. Zerado a cada fluxo novo; dispensado quando a
    // justificativa existe (o vínculo passa a vir dela).
    const [pesquisaWatchId, setPesquisaWatchId] = useState<string | null>(null)
    const [pesquisaFluxo, setPesquisaFluxo] = useState<PesquisaVinculadaOut | null>(null)
    const pesquisaWatchRef = useRef<string | null>(null)
    pesquisaWatchRef.current = pesquisaWatchId

    // Notifier (padrão da casa): em vez de polling, o backend avisa por websocket
    // quando a justificativa conclui. Aqui consumimos a mensagem do módulo e
    // guardamos qual id estamos aguardando (pendenteRef). O canal da Pesquisa de
    // Preços entra só p/ refetchar a pesquisa VINCULADA quando ela conclui.
    const {caronaSocketMessage, priceResearchSocketMessage} = useContext(HistoryContext)
    const pendenteRef = useRef<string | null>(null)
    // Correlação da REDAÇÃO pela IA (evento ia_texto) — ref própria, por VALOR
    // (id da justificativa aguardando a redação), como nos demais fluxos.
    const pendenteIaRef = useRef<string | null>(null)
    // Registro ATUALMENTE aberto na tela de justificativa: push/refetch atrasado
    // de um registro anterior NUNCA escreve por cima do aberto (troca A→B).
    const justificativaAtualRef = useRef<string | null>(null)
    // Timer do ÚNICO refetch de reconciliação da redação (fallback se o push não
    // chegar) — cancelado quando o push resolve primeiro, ou no unmount.
    const iaReconTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    // Espelha `view` p/ os flushes assíncronos (ex.: rascunho no unmount) checarem
    // a tela ATUAL, não a capturada no momento do clique.
    // Correlação independente da sugestão de "demanda concreta" — ref e effect
    // próprios, sem cruzar com pendenteRef/justificativa_id acima.
    const pendenteSugestaoRef = useRef<string | null>(null)
    // Correlação das sugestões de TERMO (fluxo do "0 resultados", hoje assíncrono no
    // backend: SQS + callback). Ref própria, como os demais fluxos. SEM poll de
    // reconciliação: é best-effort puro — sem o push, a lista simplesmente não
    // aparece (mesma degradação de quando o webhook síncrono antigo caía).
    const pendenteTermosRef = useRef<string | null>(null)
    // Enquanto true, a lista de resultados mostra "processando" no lugar do vazio —
    // um "nenhuma ata encontrada" que vira sugestão 3s depois é pior que esperar.
    const [sugestoesPendentes, setSugestoesPendentes] = useState(false)
    const termosTetoRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    // Timer do poll de reconciliação da sugestão (setTimeout recursivo). Cancelado
    // quando o WS resolve primeiro, quando o teto é atingido, ou no unmount.
    const sugestaoPollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    // Espelha `view` p/ o toast de erro da sugestão (que pode chegar bem depois, via
    // WS ou fallback) checar a tela ATUAL, não a capturada no momento do clique.
    const viewRef = useRef(view)
    viewRef.current = view

    // ---- Rascunho (histórico com "salvar pela metade" + retomar) ----------
    // Id do rascunho ATIVO deste fluxo (novo salvo OU retomado): 1º save = POST,
    // seguintes = PUT no mesmo id; ao concluir, entra no POST /justificativas como
    // rascunho_id (promove o MESMO registro, sem duplicar). Ref, não estado: só
    // orquestra chamadas — nenhuma parte da UI depende do valor.
    const rascunhoIdRef = useRef<string | null>(null)
    // POST de criação do rascunho EM VOO, como PROMISE aguardável (resolve com o id,
    // ou null se a criação falhou). Quem precisar do id no meio da criação — um flush
    // concorrente (perderia dado) ou o concluir (criaria registro órfão + crédito
    // extra) — encadeia nesta promise em vez de virar no-op / disparar sem rascunho_id.
    const criacaoRascunhoRef = useRef<Promise<string | null> | null>(null)
    // Timer do autosave debounced (rearmado a cada mudança; cancelado ao concluir/sair).
    const rascunhoDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    // Autosave suspenso: após concluir (rascunho promovido — PUT daria 409) ou ao
    // reabrir uma CONCLUÍDA (que nunca salva rascunho). Rearmado em iniciar/retomar.
    const pausarAutosaveRef = useRef(false)
    // estado_parcial (JSON opaco de UI/wizard) — round-trip verbatim; o painel de
    // geração não tem passos/abas, então fica {} (não inventamos campos).
    const estadoParcialRef = useRef<Record<string, unknown>>({})
    // Espelhos p/ os flushes (troca de view / unmount) lerem o estado MAIS RECENTE.
    const respostasRef = useRef(respostas)
    respostasRef.current = respostas
    const evidenciasRef = useRef(evidencias)
    evidenciasRef.current = evidencias
    const ataRef = useRef(ata)
    ataRef.current = ata
    // Termo de busca corrente (o que o usuário PESQUISOU, ex.: "café") — espelhado
    // p/ o autosave gravá-lo em estado_parcial.busca_termo, que o backend usa como
    // 1º critério do nome do caso na lista "Minhas Pesquisas".
    const termoRef = useRef(termo)
    termoRef.current = termo
    const reabertaRef = useRef(reaberta)
    reabertaRef.current = reaberta
    const prevViewRef = useRef<View>(view)
    // Token monotônico de "contexto de busca corrente": incrementado em TODA busca
    // manual (handleBuscar) e em TODO Retomar (antes da re-busca de alternativas).
    // A re-busca DESTACADA do Retomar captura o token no dispatch e só aplica o
    // resultado se ainda for o corrente — descarta resposta obsoleta (reordenação de
    // rede): um 2º Retomar OU uma busca manual invalidam a re-busca pendente.
    const buscaTokenRef = useRef(0)

    // Recebeu aviso do notifier (websocket) de que a justificativa que estávamos
    // aguardando concluiu → busca o resultado final e atualiza a tela. Substitui o
    // polling: nada de ficar perguntando "já terminou?" ao backend.
    useEffect(() => {
        const msg: any = caronaSocketMessage || {}
        const id = msg.justificativa_id || msg.project_id
        if (!id || id !== pendenteRef.current) return
        if (msg.status && msg.status !== 'PROCESSANDO') {
            pendenteRef.current = null
            setGerando(false)
            caronaApi
                .obterJustificativa(id)
                .then(setJustificativa)
                .catch(() => {})
        }
    }, [caronaSocketMessage])

    // ---- Redação pela IA (evento ia_texto do módulo ata-inteligente) ----
    // O texto exibido é SEMPRE o redigido pela IA; enquanto PROCESSANDO a área do
    // texto mostra o estado de redação. A conclusão chega por PUSH (mesmo canal
    // do notifier tratado acima; correlação por VALOR contra pendenteIaRef — o
    // notifier pode descartar campos fora dos schemas dele, ex.: `evento`).

    function pararReconciliacaoIa() {
        if (iaReconTimerRef.current !== null) {
            clearTimeout(iaReconTimerRef.current)
            iaReconTimerRef.current = null
        }
    }

    /** Aplica o detalhe refetchado; devolve true quando a redação chegou a estado
     * terminal (CONCLUIDO/FALHOU) e a espera pode ser encerrada. Só aplica sobre
     * o registro ATUALMENTE aberto E ainda aguardado — push/timer atrasado do
     * registro A nunca escreve por cima do B (dados misturados, edições do
     * contentEditable destruídas). */
    function aplicarIaTexto(id: string, j: JustificativaOut): boolean {
        if (pendenteIaRef.current !== id || justificativaAtualRef.current !== id) return false
        setJustificativa(j)
        if (j.ia_texto_status && j.ia_texto_status !== 'PROCESSANDO') {
            pendenteIaRef.current = null
            pararReconciliacaoIa()
            setIaEsperaEsgotada(false)
            return true
        }
        return false
    }

    /** Arma a espera pelo push do evento ia_texto + a reconciliação local: 1º
     * refetch com MARGEM (150s — além do timeout de 120s do backend) e no máximo
     * MAIS UM refetch final (+120s). Esgotados os dois, estado honesto na tela
     * (Atualizar manual) — NUNCA loop de polling. O push, se chegar depois,
     * ainda resolve (a ref segue armada). */
    function aguardarIaTexto(id: string) {
        pendenteIaRef.current = id
        setIaEsperaEsgotada(false)
        pararReconciliacaoIa()
        iaReconTimerRef.current = setTimeout(async () => {
            iaReconTimerRef.current = null
            if (pendenteIaRef.current !== id) return
            try {
                if (aplicarIaTexto(id, await caronaApi.obterJustificativa(id))) return
            } catch {
                /* best-effort: ainda há o refetch final */
            }
            if (pendenteIaRef.current !== id) return
            iaReconTimerRef.current = setTimeout(async () => {
                iaReconTimerRef.current = null
                if (pendenteIaRef.current !== id) return
                try {
                    if (aplicarIaTexto(id, await caronaApi.obterJustificativa(id))) return
                } catch {
                    /* estado honesto abaixo */
                }
                if (pendenteIaRef.current !== id) return
                // Saída honesta: sem mais timers. O usuário atualiza manualmente;
                // o read-repair do backend marca FALHOU no GET quando o TTL vencer
                // (e o Refazer aparece), ou o push tardio ainda resolve.
                setIaEsperaEsgotada(true)
            }, IA_TEXTO_RECONCILIACAO_FINAL_MS)
        }, IA_TEXTO_RECONCILIACAO_MS)
    }

    /** Refetch manual/imediato do detalhe (ex.: POST /ia-texto respondeu 200
     * CONCLUIDO — outra aba já redigiu; ou o botão Atualizar do estado honesto).
     * Estado terminal encerra a espera e limpa o aviso de demora. */
    async function recarregarJustificativa(id: string) {
        try {
            const j = await caronaApi.obterJustificativa(id)
            if (justificativaAtualRef.current !== id) return // outro registro aberto
            setJustificativa(j)
            if (j.ia_texto_status && j.ia_texto_status !== 'PROCESSANDO') {
                if (pendenteIaRef.current === id) pendenteIaRef.current = null
                pararReconciliacaoIa()
                setIaEsperaEsgotada(false)
            }
        } catch {
            /* best-effort */
        }
    }

    // Push do notifier para a redação: mesmo payload genérico (project_id); a
    // correlação é por valor contra pendenteIaRef — não colide com os fluxos de
    // justificativa/sugestão acima (refs próprias por fluxo).
    useEffect(() => {
        const msg: any = caronaSocketMessage || {}
        const id = msg.justificativa_id || msg.project_id
        if (!id || id !== pendenteIaRef.current) return
        caronaApi
            .obterJustificativa(id)
            .then((j) => aplicarIaTexto(id, j))
            .catch(() => {
                /* best-effort: o refetch de reconciliação ainda pode resolver */
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caronaSocketMessage])

    // ---- Pesquisa de Preços VINCULADA em andamento ----
    // A pesquisa vinculada está pendente numa tela que DEPENDE dela? Vale para a
    // justificativa (card com valores) E para o painel de geração (o CTA espera a
    // pesquisa em andamento — sem refetch aqui, o "Aguardando a Pesquisa" nunca
    // destravaria ao voltar via "Ajustar dados"). Fonte de verdade:
    // justificativa.pesquisa_precos do GET — os campos do callback são opcionais.
    const pesquisaVinculadaPendente = Boolean(
        (view === 'justificativa' || view === 'gerar') &&
        justificativa?.pesquisa_precos &&
        !justificativa.pesquisa_precos.concluida
    )
    const pesquisaVinculadaIdRef = useRef<string | null>(null)
    pesquisaVinculadaIdRef.current = justificativa?.pesquisa_precos?.id || null
    const pesquisaVinculadaPendenteRef = useRef(pesquisaVinculadaPendente)
    pesquisaVinculadaPendenteRef.current = pesquisaVinculadaPendente

    // Poll LEVE (5s) SÓ enquanto a tela da justificativa está aberta com a
    // pesquisa vinculada pendente — cleanup ao concluir/trocar de tela/desmontar.
    // O refetch é o mesmo GET guardado (recarregarJustificativa: só aplica se o
    // registro aberto ainda for o mesmo).
    useEffect(() => {
        if (!pesquisaVinculadaPendente || !justificativa?.id) return
        const id = justificativa.id
        const t = setInterval(() => {
            void recarregarJustificativa(id)
        }, PESQUISA_VINCULADA_POLL_MS)
        return () => clearInterval(t)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pesquisaVinculadaPendente, justificativa?.id])

    // Push do carona-service (evento "pesquisa" do módulo ata-inteligente — o
    // callback da Pesquisa gravou os valores na justificativa): correlação por id
    // contra o registro ABERTO; refetch só com a pesquisa pendente.
    useEffect(() => {
        const msg: any = caronaSocketMessage || {}
        const id = msg.justificativa_id || msg.project_id
        if (!id || id !== justificativaAtualRef.current) return
        if (!pesquisaVinculadaPendenteRef.current) return
        void recarregarJustificativa(id)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caronaSocketMessage])

    // Push do canal da Pesquisa de Preços (pesquisa CONCLUIDO): quando a mensagem
    // é da pesquisa vinculada (ou não identifica a pesquisa mas anuncia conclusão),
    // refetcha a justificativa aberta para trazer os valores.
    useEffect(() => {
        if (!pesquisaVinculadaPendenteRef.current) return
        const id = justificativaAtualRef.current
        if (!id) return
        const msg: any = priceResearchSocketMessage || {}
        const pesquisaId = msg.pesquisa_id || msg.project_id
        if (pesquisaId && pesquisaVinculadaIdRef.current && pesquisaId !== pesquisaVinculadaIdRef.current) return
        if (!pesquisaId && msg.status !== 'CONCLUIDO') return
        void recarregarJustificativa(id)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priceResearchSocketMessage])

    /** Lê o registro do VÍNCULO do fluxo (rascunho/justificativa vigiado) e espelha
     * a pesquisa nele — best-effort; aplica só se a vigilância ainda for a mesma. */
    async function lerVinculoFluxo(id: string) {
        try {
            const j = await caronaApi.obterJustificativa(id)
            if (pesquisaWatchRef.current !== id) return
            setPesquisaFluxo(j.pesquisa_precos || null)
        } catch {
            /* best-effort: a próxima rodada do poll tenta de novo */
        }
    }

    // Poll LEVE do vínculo do FLUXO (pré-geração): com o painel 'gerar' aberto,
    // vigilância armada e sem justificativa carregada, refetcha o registro do
    // vínculo até a pesquisa aparecer e CONCLUIR — é o que destrava o "Aguardando
    // a Pesquisa de Preços" e traz os valores para a geração. Cleanup ao trocar
    // de tela/desmontar; para sozinho quando conclui.
    useEffect(() => {
        if (view !== 'gerar' || !pesquisaWatchId || justificativa) return
        if (pesquisaFluxo?.concluida) return
        const id = pesquisaWatchId
        void lerVinculoFluxo(id)
        const t = setInterval(() => void lerVinculoFluxo(id), PESQUISA_VINCULADA_POLL_MS)
        return () => clearInterval(t)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, pesquisaWatchId, justificativa, pesquisaFluxo?.concluida])

    // Push do callback da Pesquisa para o registro VIGIADO (o carona-service grava
    // os valores no rascunho/justificativa do vínculo e avisa): refetch imediato —
    // o poll acima é só a rede de segurança.
    useEffect(() => {
        const msg: any = caronaSocketMessage || {}
        const id = msg.justificativa_id || msg.project_id
        if (!id || id !== pesquisaWatchRef.current) return
        void lerVinculoFluxo(id)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caronaSocketMessage])

    // Encerra a espera das sugestões de termo (push resolveu, falhou ou estourou o
    // teto). Seguro de chamar mais de uma vez.
    function pararEsperaTermos() {
        if (termosTetoRef.current !== null) {
            clearTimeout(termosTetoRef.current)
            termosTetoRef.current = null
        }
        setSugestoesPendentes(false)
    }

    // Sugestões de TERMO (0 resultados): o push do notifier confirma que o n8n
    // respondeu; o GET traz a lista. Correlação por VALOR contra a ref própria — cai
    // em `project_id` porque o notifier valida contra schemas fechados e descarta
    // `sugestoes_termos_id` em silêncio (mesma nota do effect da demanda acima).
    useEffect(() => {
        const msg: any = caronaSocketMessage || {}
        const id = msg.sugestoes_termos_id || msg.project_id
        if (!id || id !== pendenteTermosRef.current) return
        if (msg.status === 'PROCESSANDO') return
        caronaApi
            .obterSugestoesTermos(id)
            .then((s) => {
                // Busca nova já assumiu a ref: resposta tardia NUNCA sobrescreve a tela.
                if (pendenteTermosRef.current !== id) return
                pendenteTermosRef.current = null
                setSugestoes(s.status === 'APTA' ? s.sugestoes || [] : [])
                pararEsperaTermos()
            })
            .catch(() => {
                setSugestoes([])
                pararEsperaTermos()
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caronaSocketMessage])

    // Garante que os timers de reconciliação (redação da IA) não continuem
    // rodando (e tentando setState) depois que o componente desmontar.
    useEffect(() => {
        return () => {
            if (termosTetoRef.current !== null) clearTimeout(termosTetoRef.current)
            pararReconciliacaoIa()
            if (rascunhoDebounceRef.current !== null) {
                clearTimeout(rascunhoDebounceRef.current)
                rascunhoDebounceRef.current = null
            }
            // Flush best-effort ao desmontar — cobre o handoff p/ a Pesquisa de
            // Preços, que deixa o módulo com a geração pela metade ("parei pra
            // fazer uma pesquisa"). persistirRascunho valida reaberta/pausa/conteúdo.
            if (viewRef.current === 'gerar') {
                void persistirRascunho(respostasRef.current, evidenciasRef.current, ataRef.current)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Autosave DEBOUNCED: enquanto respostas/evidências mudam no painel de geração
    // de um fluxo editável (não numa CONCLUÍDA reaberta), grava o rascunho após a pausa.
    useEffect(() => {
        if (view !== 'gerar' || reaberta || !ata) return
        if (pausarAutosaveRef.current) return
        if (!entrevistaTemConteudo(respostas, evidencias)) return
        const t = setTimeout(() => {
            rascunhoDebounceRef.current = null
            void persistirRascunho(respostas, evidencias, ata)
        }, RASCUNHO_AUTOSAVE_MS)
        rascunhoDebounceRef.current = t
        return () => clearTimeout(t)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [respostas, evidencias, view, reaberta, ata])

    // Save ao SAIR do painel de geração para outra tela DO MÓDULO (voltar,
    // histórico…). A ida para 'justificativa' é a CONCLUSÃO (tratada em
    // gerarJustificativa com rascunho_id) — nunca autosava por cima dela.
    useEffect(() => {
        const prev = prevViewRef.current
        prevViewRef.current = view
        if (prev === 'gerar' && view !== 'gerar' && view !== 'justificativa') {
            if (rascunhoDebounceRef.current !== null) {
                clearTimeout(rascunhoDebounceRef.current)
                rascunhoDebounceRef.current = null
            }
            void persistirRascunho(respostasRef.current, evidenciasRef.current, ataRef.current)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view])

    // ---- Handoff do Levantamento de Mercado (bloco ADITIVO e isolado) ------
    // A busca de atas é pré-preenchida com o termo do OBJETO e disparada. O
    // handoff chega por DOIS caminhos:
    //   • NOVA ABA (fluxo atual): params na querystring
    //     (?origem=levantamento&termo_busca=...) — uma aba nova NÃO herda o
    //     state de navegação do react-router, por isso vão na URL.
    //   • router state (window.history.state.usr): navegação same-tab (compat).
    // Lido direto (sem hook de Router) p/ o módulo seguir montável fora de um
    // Router; consumido UMA única vez (querystring limpa e/ou usr zerado via
    // replaceState). Sem handoff, nada aqui muda o módulo.
    const handoffLevantamentoRef = useRef(false)
    useEffect(() => {
        if (handoffLevantamentoRef.current) return
        const params = new URLSearchParams(window.location.search)
        const qsOrigem = params.get('origem')
        const qsTermo = params.get('termo_busca')
        const st = ((window.history.state as any)?.usr ?? null) as
            | {origem?: string; termo_busca?: string}
            | null
        const origem = qsOrigem ?? st?.origem
        const termo = qsTermo ?? st?.termo_busca
        if (origem !== 'levantamento' || !termo) return
        handoffLevantamentoRef.current = true
        // Consome o handoff UMA vez: limpa a querystring (reload não redispara)
        // e/ou zera o usr do router state (navegação same-tab).
        if (qsOrigem || qsTermo) {
            window.history.replaceState(window.history.state, '', window.location.pathname)
        }
        if (st) {
            window.history.replaceState({...(window.history.state as any), usr: null}, '')
        }
        handleBuscar({
            objeto: String(termo),
            esferaGerenciador: '',
            uf: '',
            vigentes: true,
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // ---- Navegação REVERSA da Pesquisa de Preços (mini-contrato da onda 2) ----
    // A Pesquisa volta com navigate('/ata-inteligente', {state: {origem: 'pesquisa',
    // justificativa_id}}). O state chega em window.history.state.usr (mesma
    // leitura direta do handoff do Levantamento — o módulo segue montável fora de
    // um Router) e é consumido UMA vez (usr zerado via replaceState, como o
    // PriceResearch faz no handoff dele). Abre a justificativa pelo MESMO caminho
    // do "Abrir" do histórico (reabrir).
    const handoffPesquisaRef = useRef(false)
    useEffect(() => {
        if (handoffPesquisaRef.current) return
        const st = ((window.history.state as any)?.usr ?? null) as
            | {origem?: string; justificativa_id?: string}
            | null
        if (st?.origem !== 'pesquisa' || !st.justificativa_id) return
        handoffPesquisaRef.current = true
        window.history.replaceState({...(window.history.state as any), usr: null}, '')
        void reabrir(String(st.justificativa_id))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    async function handleBuscar(form: BuscaFormValues, tentativa = 1) {
        // Novo contexto de busca: invalida qualquer re-busca de alternativas pendente
        // (de um Retomar anterior) — a resposta tardia dela não pode sobrescrever esta.
        buscaTokenRef.current += 1
        setTermo(form.objeto)
        setUltimaBusca(form)
        setSugestoes([])
        pendenteTermosRef.current = null   // invalida o push de uma busca anterior
        pararEsperaTermos()
        setIrps([])
        setBuscando(true)
        setErroBusca(null)
        navegar('resultados')
        // A repetição automática (fonte caída) mantém o spinner de busca ligado —
        // o finally só desliga quando esta rodada NÃO reagendou outra.
        let vaiRepetir = false
        // IRP em paralelo (rápido, do nosso banco): trilho "Participar". Best-effort,
        // não bloqueia as atas (PNCP ao vivo). Aparece como seção própria na lista.
        caronaApi.buscarIrp(form.objeto, form.uf || undefined).then(setIrps).catch(() => setIrps([]))
        try {
            // Busca AMPLA: sem esfera_aderente (não aplica vedação). Inclui as atas
            // "adesão permitida" (verde) E "condicional / a confirmar" (amarela) —
            // o PNCP raramente confirma a permissão; o gestor confirma na entrevista.
            // Exclui só as vedadas e as vencidas.
            const data = await caronaApi.buscar({
                objeto: form.objeto,
                esfera_aderente: null,
                uf: form.uf || null,
                apenas_vigentes: form.vigentes,
                apenas_permitidas: false,
                incluir_vedadas: false,
                limite: 100,
            })
            let res = data.resultados || []
            if (form.esferaGerenciador) {
                res = res.filter((a) => a.esfera_codigo === form.esferaGerenciador)
            }
            setResultados(res)
            setOrigem(data.origem || 'cache')
            // Fonte oficial caiu SEM nada para mostrar: o front INSISTE SOZINHO
            // (até 3 tentativas, com pausa crescente p/ o PNCP respirar — regra do
            // fundador: demorar mais e tentar de novo, nunca o vazio convicto).
            // O token invalida a repetição se o usuário buscar outra coisa no meio.
            if (data.fonte_indisponivel && res.length === 0 && tentativa < 3) {
                const token = buscaTokenRef.current
                vaiRepetir = true
                setTimeout(() => {
                    if (buscaTokenRef.current === token) void handleBuscar(form, tentativa + 1)
                }, 1200 * tentativa)
                return
            }
            // Esgotou as tentativas automáticas: aviso honesto com retry manual —
            // jamais um "0 resultados" convicto em cima de queda do PNCP.
            if (data.fonte_indisponivel && res.length === 0) {
                setErroBusca(
                    'O PNCP (fonte oficial) não respondeu mesmo após 3 tentativas — os resultados podem existir. Tente novamente em instantes.'
                )
            }
            // 0 resultados → pede sugestões de termo (IA via n8n), best-effort. O
            // backend só ENFILEIRA (SQS) e responde 202: a lista chega pelo push do
            // notifier (websocket), tratado no effect acima. A tela fica em
            // "processando" desde JÁ (antes do 202) para não piscar um "nenhuma ata
            // encontrada" que seria substituído pelas sugestões segundos depois.
            if (res.length === 0) {
                setSugestoesPendentes(true)
                const token = buscaTokenRef.current
                termosTetoRef.current = setTimeout(() => {
                    if (buscaTokenRef.current === token) pararEsperaTermos()
                }, SUGESTOES_TERMOS_TETO_MS)
                caronaApi
                    .sugestoes(form.objeto)
                    .then((job) => {
                        pendenteTermosRef.current = job.id
                    })
                    .catch(() => {
                        setSugestoes([])
                        pararEsperaTermos()
                    })
            }
        } catch (e: any) {
            const status = e?.response?.status
            setErroBusca(
                status === 401 || status === 403
                    ? 'Não autorizado pela API (verifique o token/DEV_AUTH_KEY do carona-service).'
                    : `Não foi possível falar com a API da Ata Inteligente (${e?.message || 'erro'}).`
            )
            setResultados([])
        } finally {
            if (!vaiRepetir) setBuscando(false)
        }
    }

    async function abrirDetalhe(numeroControle: string) {
        // Navegação otimista: mostra o resumo (já conhecido) na hora e carrega os
        // itens/saldo do PNCP dentro da própria página de detalhe. Com a busca em
        // mãos, o ?termo= faz o backend marcar o item ADERENTE (recomendado).
        const resumo = resultados.find((a) => a.numero_controle_pncp === numeroControle) || null
        setResumoSel(resumo)
        setCarregandoAta(true)
        setAta(null)
        navegar('detalhe')
        try {
            const data = await caronaApi.detalhe(numeroControle, undefined, termoRef.current)
            setAta(data)
        } catch (e: any) {
            toast.error(`Falha ao carregar o detalhe da ata: ${e?.message || 'erro'}`)
            voltar()
        } finally {
            setCarregandoAta(false)
        }
    }

    async function iniciarGeracao(numeroControle: string, selecao?: SelecaoItens) {
        let a = ata
        if (!a || a.numero_controle_pncp !== numeroControle) {
            setCarregandoAta(true)
            try {
                a = await caronaApi.detalhe(numeroControle, undefined, termoRef.current)
                setAta(a)
            } catch (e: any) {
                toast.error(`Falha ao carregar a ata: ${e?.message || 'erro'}`)
                setCarregandoAta(false)
                return
            }
            setCarregandoAta(false)
        }
        // mantém a esfera escolhida antes (ou a inferida do órgão), corrigível no
        // painel — e carrega a SELEÇÃO de itens feita no detalhe da ata.
        setRespostas((r) => ({
            ...defaultRespostas(r.esfera_aderente),
            ...(selecao ? {
                itens_selecionados: selecao.itens,
                itens_selecionados_descricoes: selecao.descricoes,
                quantidades_desejadas: selecao.quantidades,
            } : {}),
        }))
        setEvidencias({})
        // Fluxo NOVO: zera o ciclo do rascunho (um novo será criado só quando houver
        // conteúdo real) e religa o autosave.
        rascunhoIdRef.current = null
        criacaoRascunhoRef.current = null
        pausarAutosaveRef.current = false
        estadoParcialRef.current = {}
        // Fluxo novo: zera a vigilância do vínculo com a Pesquisa (cada adesão tem o seu).
        setPesquisaWatchId(null)
        setPesquisaFluxo(null)
        // Documento de contexto zera junto com o fluxo (cada adesão tem o seu);
        // a lista de já enviados carrega em paralelo (best-effort) p/ reaproveitar.
        // Blindado contra QUALQUER retorno (Promise.resolve embrulha throw síncrono e
        // Array.isArray descarta payload inesperado — ex.: mocks parciais nos testes).
        setDocumento(null)
        setErroDoc(null)
        Promise.resolve()
            .then(() => caronaApi.listarDocumentos())
            .then((ds) => setDocumentosDisponiveis(Array.isArray(ds) ? ds : []))
            .catch(() => setDocumentosDisponiveis([]))
        navegar('gerar')
    }

    /** Troca a ata ESCOLHIDA por uma alternativa mais vantajosa AINDA no painel de
     * geração (o gestor decidiu adotar antes de gerar). Carrega o detalhe da nova
     * ata e PRESERVA as respostas já feitas; só as quantidades por item são
     * redefinidas (os itens da nova ata são outros). As alternativas e a comparação
     * recomputam relativas à nova escolhida: `alternativasAtas` deriva de
     * `resultados` menos a escolhida (a ata antiga volta a aparecer como
     * alternativa), e a ComparacaoAtas reseta ao ver a nova ata. O autosave grava
     * o rascunho já apontando para a nova ata. */
    async function adotarAta(numeroControle: string) {
        const alvo = (numeroControle || '').trim()
        if (!alvo || ataRef.current?.numero_controle_pncp === alvo) return
        try {
            const nova = await caronaApi.detalhe(alvo, undefined, termoRef.current)
            // Preserva tudo (demanda, toggles, evidências, alternativas consideradas);
            // zera as quantidades por item — os números de item da nova ata diferem.
            setRespostas((r) => ({...r, quantidades_desejadas: {}}))
            setAta(nova)
            setResumoSel(resultados.find((a) => a.numero_controle_pncp === alvo) || nova)
            toast.success(
                `Ata escolhida trocada para ${nova.numero_ata || 'a alternativa'} — revise as quantidades por item.`
            )
        } catch (e: any) {
            toast.error(`Não foi possível trocar a ata (${e?.message || 'erro'}).`)
        }
    }

    /** Upload do ETP/DFD/TR: o backend extrai o texto na hora (síncrono). Estados da
     * tela: extraindo… → pronto (nome do arquivo) | erro amigável (detail do backend). */
    async function enviarDocumento(file: File) {
        setErroDoc(null)
        setEnviandoDoc(true)
        try {
            const d = await caronaApi.enviarDocumento(file)
            setDocumento(d)
            setDocumentosDisponiveis((prev) => [d, ...prev.filter((x) => x.id !== d.id)])
        } catch (err: any) {
            setDocumento(null)
            setErroDoc(err?.response?.data?.detail
                || 'Não foi possível ler o documento. Envie um PDF ou DOCX de até 10MB.')
        } finally {
            setEnviandoDoc(false)
        }
    }

    function selecionarDocumento(id: string) {
        setErroDoc(null)
        setDocumento(documentosDisponiveis.find((d) => d.id === id) || null)
    }

    // Pesquisa de Preços vinculada à justificativa DESTA ata (o backend enriquece o
    // GET com status/valores — leitura best-effort). Guarda o vínculo p/ o card e
    // p/ o carry-over ao regerar (regerar reavalia a vantajosidade com os valores).
    const pesquisaVinculada =
        justificativa && ata &&
        justificativa.ata_numero_controle === ata.numero_controle_pncp
            ? justificativa.pesquisa_precos || null
            : null

    // Pesquisa do FLUXO corrente: a vinculada à justificativa aberta OU, antes de
    // gerar, a VIGIADA no registro do vínculo (rascunho do handoff) — é ela que o
    // painel de geração exibe/espera e que entra como carry-over no POST.
    const pesquisaDoFluxo = pesquisaVinculada || pesquisaFluxo

    // Outras atas do MESMO objeto que a busca já trouxe: as alternativas a comparar
    // na seção "Análise de alternativas e justificativa da escolha" do ETP (Acórdão
    // 547/2026-TCU). Sem custo/fonte nova — reusa os resultados em mãos, excluindo a
    // ata escolhida.
    const alternativasAtas = useMemo(() => {
        const escolhida = ata?.numero_controle_pncp || resumoSel?.numero_controle_pncp || ''
        return resultados.filter((a) => a.numero_controle_pncp !== escolhida)
    }, [resultados, ata, resumoSel])

    /** A BUSCA ORIGINAL do usuário (o que ele digitou, ex.: "café"): estado termo/
     * termoRef ou, ao retomar, o busca_termo salvo no estado_parcial. "" quando não
     * há busca conhecida (ex.: registro reaberto do histórico). */
    function buscaOriginal(): string {
        const t = (termoRef.current || '').trim()
        if (t) return t
        const salvo = estadoParcialRef.current?.busca_termo
        return typeof salvo === 'string' ? salvo.trim() : ''
    }

    /** Handoff ao módulo de Pesquisa de Preços (mesmo contrato do Levantamento de
     * Mercado): pré-preenche com a BUSCA ORIGINAL do usuário (o vínculo nasce do
     * que ele pesquisou, não da descrição longa do item da ata); só cai no termo
     * enxuto do item quando a busca é vazia/curta (<5 chars — mínimo exigido pelo
     * ItemInput da Pesquisa). Quando já existe justificativa, o módulo vincula a
     * pesquisa criada de volta (pós-202). */
    async function pesquisarPrecos() {
        if (!ata) return
        // A ABA abre AGORA, no gesto do clique. window.open depois de um await é
        // engolido pelo bloqueador de pop-ups (Chrome e Safari só confiam em abertura
        // SÍNCRONA ao gesto do usuário) — e era exatamente o caso do primeiro clique:
        // sem rascunho ainda, o handler esperava o POST do rascunho e o open era
        // bloqueado em silêncio. Sintoma medido em 18/08/2026: "clica e não abre".
        // A aba nasce em branco e recebe a URL no fim; se ainda assim for bloqueada,
        // o fallback navega na MESMA aba — perder a aba extra é melhor que perder o
        // clique do gestor.
        const aba = window.open('', '_blank')
        if (aba) aba.opener = null // mesma higiene do 'noopener' de antes
        const busca = buscaOriginal()
        // ID BILATERAL GARANTIDO (bug real do dono: pesquisa disparada da
        // ENTREVISTA, antes de gerar a justificativa, nascia SEM vínculo — id
        // vazio → nada se ligava). Sem justificativa gerada, o vínculo é pelo
        // RASCUNHO (D2: mesmo registro que será promovido ao concluir — o
        // pesquisa_precos_id sobrevive à promoção; o backend vincula em
        // qualquer status). Sem rascunho ainda → cria um agora (payload já
        // carrega ata + busca_termo; respostas podem estar vazias — o clique
        // em "pesquisar preços" é intenção real, não rascunho-fantasma).
        let vinculoId = justificativa?.id || rascunhoIdRef.current
        if (!vinculoId && criacaoRascunhoRef.current) {
            vinculoId = await criacaoRascunhoRef.current
        }
        if (!vinculoId && !reabertaRef.current) {
            try {
                const criado = await caronaApi.salvarRascunho(
                    montarPayloadRascunho(respostas, evidencias, ata)
                )
                vinculoId = criado?.id || null
                if (vinculoId) rascunhoIdRef.current = vinculoId
            } catch {
                // Best-effort: sem id, a pesquisa abre sem vínculo (comportamento
                // antigo) — nunca bloqueia o gestor de pesquisar preços.
            }
        }
        // VÍNCULO PROFUNDO no fluxo pré-geração: arma a vigilância do registro do
        // vínculo — quando a pesquisa for criada lá, o card aparece aqui e o Gerar
        // espera a conclusão (com justificativa aberta, o vínculo já vem dela).
        if (vinculoId && !justificativa) {
            setPesquisaWatchId(vinculoId)
        }
        // NOVA ABA (pedido do dono): a Ata fica intacta — o gestor não perde a
        // entrevista/justificativa em andamento, e o card da pesquisa vinculada
        // segue acompanhando aqui (spinner/orçamento). window.open não carrega
        // state do router → o handoff viaja por query string (o leitor da
        // Pesquisa aceita as duas portas e limpa a URL ao consumir).
        // Descrição EXATA do(s) item(ns) que está(ão) sendo aderido(s) (regra do
        // fundador, 01/09/2026): a pesquisa nasce do item real ("Livro Didático
        // grau: ensino superior…"), nunca do termo curto da busca ("livro").
        const descricaoItens = (respostas.itens_selecionados || [])
            .map((n) => ata.itens.find((it) => it.numero_item === n)?.descricao?.trim() || '')
            .filter(Boolean)
            .join('; ')
        const params = new URLSearchParams({
            origem: 'carona',
            justificativa_id: vinculoId || '',
            descricao_solucao: descricaoItens
                || (busca.length >= 5 ? busca : termoPesquisa(ata, respostas, termo)),
        })
        const url = `/pesquisa-precos?${params.toString()}`
        if (aba && !aba.closed) {
            aba.location.href = url
        } else {
            navigate(url)
        }
    }

    /** Abre o módulo de Pesquisa de Preços SEM handoff (consultar a vinculada). */
    function abrirModuloPesquisas() {
        navigate('/pesquisa-precos')
    }

    async function gerarJustificativa() {
        if (!ata) return
        // Conclusão: cancela qualquer autosave pendente ANTES do await, senão um PUT
        // atrasado promoveria a corrida com a conclusão (409 no rascunho já promovido).
        if (rascunhoDebounceRef.current !== null) {
            clearTimeout(rascunhoDebounceRef.current)
            rascunhoDebounceRef.current = null
        }
        // Desabilita o botão já (inclui a espera pela criação abaixo).
        setGerando(true)
        let rascId = rascunhoIdRef.current
        // Criação do rascunho ainda EM VOO no clique de Gerar: aguarda o id para
        // PROMOVER o mesmo registro. Sem isso, o POST /justificativas sairia sem
        // rascunho_id → cria uma justificativa nova + deixa um rascunho órfão do mesmo
        // objeto no histórico + cobra crédito de novo. Best-effort: falha → fluxo novo.
        if (!rascId && criacaoRascunhoRef.current) {
            try {
                rascId = await criacaoRascunhoRef.current
            } catch {
                rascId = null
            }
        }
        const evidenciasList: EvidenciaIn[] = evidenciasToBackend(evidencias)
        // Busca original do usuário: o backend persiste e ANCORA o casamento de
        // itens da geração nela (enviar sempre que houver).
        const buscaTermo = buscaOriginal()

        const req: CriarJustificativaRequest = {
            ata_numero_controle: ata.numero_controle_pncp,
            modo: 'v1',
            respostas: {...respostas} as unknown as Record<string, unknown>,
            evidencias: evidenciasList,
            ...(buscaTermo ? {busca_termo: buscaTermo} : {}),
            // Carry-over do vínculo ao REGERAR: o backend relê a pesquisa
            // (best-effort) e reavalia a vantajosidade com os valores reais.
            ...(pesquisaDoFluxo ? {pesquisa_precos_id: pesquisaDoFluxo.id} : {}),
            // Promove o rascunho ativo (fluxo novo salvo OU retomado): o MESMO card do
            // histórico vira PROCESSANDO/APTA, sem duplicar.
            ...(rascId ? {rascunho_id: rascId} : {}),
        }

        // Navegação otimista: entra já na página da justificativa (com dicas) e
        // a verificação de conformidade é carregada ali, em tempo real.
        setJustificativa(null)
        justificativaAtualRef.current = null
        // Geração nova: zera a espera de redação anterior (a JustificativaView
        // dispara o /ia-texto automaticamente quando a redação nunca rodou).
        pendenteIaRef.current = null
        pararReconciliacaoIa()
        setIaEsperaEsgotada(false)
        setReaberta(false)              // fluxo da entrevista: auto-disparo normal
        setEntrevistaDisponivel(true)
        navegar('justificativa')
        try {
            const criada = await caronaApi.criarJustificativa(req)
            // Rascunho promovido: encerra seu ciclo (o mesmo registro agora é uma
            // justificativa) e para o autosave — um PUT posterior daria 409.
            if (rascId) {
                pausarAutosaveRef.current = true
                rascunhoIdRef.current = null
            }
            justificativaAtualRef.current = criada.id
            setJustificativa(criada)
            // O vínculo passa a viver na justificativa: encerra a vigilância do fluxo.
            setPesquisaWatchId(null)
            setPesquisaFluxo(null)
            if (criada.status === 'PROCESSANDO') {
                // Assíncrono (modo completo): o resultado chega pelo NOTIFIER (websocket),
                // tratado no useEffect acima. Sem polling. Rede de segurança única: se em
                // ~8s nada chegou (ex.: socket indisponível), reconcilia 1x via GET.
                pendenteRef.current = criada.id
                setTimeout(async () => {
                    if (pendenteRef.current !== criada.id) return
                    try {
                        const j = await caronaApi.obterJustificativa(criada.id)
                        if (pendenteRef.current === criada.id && j.status !== 'PROCESSANDO') {
                            pendenteRef.current = null
                            setJustificativa(j)
                        }
                    } catch {
                        /* best-effort */
                    }
                }, 8000)
            } else {
                // v1 (determinístico): a justificativa já vem pronta na resposta.
                pendenteRef.current = null
            }
        } catch (e: any) {
            const status = e?.response?.status
            toast.error(
                status === 406
                    ? 'Sem créditos de documento para gerar a justificativa.'
                    : `Falha ao gerar a justificativa (${e?.message || 'erro'}).`
            )
            voltar() // sem documento: volta para a tela anterior
        } finally {
            setGerando(false)
        }
    }

    /** Grava o rascunho (autosave / flush ao sair). BEST-EFFORT: nunca trava a
     * edição nem alerta o usuário — falha só é logada. 1º save = POST (guarda o id
     * em rascunhoIdRef), seguintes = PUT no mesmo id. Guarda contra rascunho
     * concluído (409 → para o autosave) e sumido (404 → recria no próximo save). */
    /** Monta o payload de rascunho a partir de um estado (respostas/evidências/ata). */
    function montarPayloadRascunho(
        r: RespostasEntrevista,
        ev: Record<string, string>,
        a: AtaDetalhe
    ): RascunhoRequest {
        // Preserva o estado_parcial existente (backend/UI) como base e grava o TERMO
        // DE BUSCA corrente sob `busca_termo` — só quando NÃO-vazio, para nunca apagar
        // um busca_termo já salvo se o termo local estiver vazio (ex.: ao retomar).
        // Best-effort: falha aqui não pode travar o autosave.
        const estadoParcial: Record<string, unknown> = {...(estadoParcialRef.current || {})}
        const buscaTermo = (termoRef.current || '').trim()
        if (buscaTermo) estadoParcial.busca_termo = buscaTermo
        return {
            ata_numero_controle: a.numero_controle_pncp,
            modo: 'v1',
            respostas: {...r} as unknown as Record<string, unknown>,
            evidencias: evidenciasToBackend(ev),
            estado_parcial: estadoParcial,
        }
    }

    async function persistirRascunho(
        r: RespostasEntrevista,
        ev: Record<string, string>,
        a: AtaDetalhe | null
    ) {
        if (pausarAutosaveRef.current || reabertaRef.current || !a) return
        if (!entrevistaTemConteudo(r, ev)) return
        try {
            if (rascunhoIdRef.current) {
                await caronaApi.atualizarRascunho(rascunhoIdRef.current, montarPayloadRascunho(r, ev, a))
                return
            }
            if (criacaoRascunhoRef.current) {
                // Criação EM VOO: em vez de virar no-op (perderia as edições feitas
                // durante a janela POST↔resposta), aguarda o id e grava o estado MAIS
                // RECENTE (refs) por cima — o motivo de o flush ao sair existir.
                const id = await criacaoRascunhoRef.current
                const aa = ataRef.current
                if (!id || !aa || pausarAutosaveRef.current || reabertaRef.current) return
                await caronaApi.atualizarRascunho(
                    id,
                    montarPayloadRascunho(respostasRef.current, evidenciasRef.current, aa)
                )
                return
            }
            // Primeira criação: guarda a PROMISE (aguardável por flush/concluir) e, ao
            // resolver, fixa o id. finally solta a ref mesmo em falha.
            const criacao = (async (): Promise<string | null> => {
                try {
                    const criado = await caronaApi.salvarRascunho(montarPayloadRascunho(r, ev, a))
                    const novoId = criado?.id || null
                    rascunhoIdRef.current = novoId
                    return novoId
                } finally {
                    criacaoRascunhoRef.current = null
                }
            })()
            criacaoRascunhoRef.current = criacao
            await criacao
        } catch (err: any) {
            const status = err?.response?.status
            if (status === 409) {
                // Registro já concluído — não volta a rascunho: encerra o autosave.
                pausarAutosaveRef.current = true
            } else if (status === 404) {
                // Rascunho sumiu (deletado/outra org): próximo save recria do zero.
                rascunhoIdRef.current = null
            }
            // Demais falhas: silenciosas (best-effort) — a edição não pode travar.
        }
    }

    async function abrirHistorico() {
        navegar('historico')
        setCarregandoHist(true)
        try {
            setHistorico(await caronaApi.listarJustificativas())
        } catch (e: any) {
            toast.error(`Falha ao carregar o histórico (${e?.message || 'erro'}).`)
        } finally {
            setCarregandoHist(false)
        }
    }

    /** RETOMAR um rascunho do histórico: reidrata respostas/evidências/estado_parcial,
     * carrega a ata e entra na entrevista em modo EDITÁVEL (não é a CONCLUÍDA reaberta
     * somente-leitura). A partir daqui o autosave dá PUT no MESMO id. */
    async function retomarRascunho(id: string) {
        // Troca de contexto: limpa qualquer espera de redação anterior (como reabrir).
        pendenteIaRef.current = null
        pararReconciliacaoIa()
        setIaEsperaEsgotada(false)
        try {
            const rasc = await caronaApi.obterRascunho(id)
            // Termo que o usuário PESQUISOU, salvo no autosave (estado_parcial) —
            // computado JÁ AQUI para o ?termo= do detalhe marcar o item aderente.
            const buscaTermoSalvo = typeof rasc.estado_parcial?.busca_termo === 'string'
                ? (rasc.estado_parcial.busca_termo as string).trim()
                : ''
            // A ata ANTES de navegar: a entrevista só renderiza com `ata` (view + ata).
            let ataDetalhe: AtaDetalhe
            try {
                ataDetalhe = await caronaApi.detalhe(rasc.ata_numero_controle, undefined, buscaTermoSalvo)
            } catch (e: any) {
                toast.error(
                    `Não foi possível carregar a ata deste rascunho (${e?.message || 'erro'}). Tente novamente.`
                )
                return
            }
            const respostasRasc = (rasc.respostas || {}) as Record<string, unknown>
            // Merge com os defaults: campos ausentes no rascunho ganham valor válido.
            // Campos ausentes no rascunho ganham o default — com a esfera INFERIDA
            // do órgão (nunca 'M' fixo: um órgão federal com rascunho antigo sem
            // esfera_aderente silenciaria a vedação do art. 86, §8º).
            setRespostas({...defaultRespostas(esferaInferida), ...respostasRasc})
            setEvidencias(evidenciasFromBackend(rasc.evidencias || []))
            estadoParcialRef.current = rasc.estado_parcial || {}
            // buscaTermoSalvo (computado acima) repopula o campo de busca ao retomar —
            // evita perdê-lo e evita o autosave seguinte sobrescrevê-lo com o objeto
            // da ata (usado só p/ alternativas).
            // Documento (ETP) pertence ao fluxo corrente; recarrega a lista disponível.
            setDocumento(null)
            setErroDoc(null)
            Promise.resolve()
                .then(() => caronaApi.listarDocumentos())
                .then((ds) => setDocumentosDisponiveis(Array.isArray(ds) ? ds : []))
                .catch(() => setDocumentosDisponiveis([]))
            setAta(ataDetalhe)
            // As "outras atas do mesmo objeto" (Bloco 7 / análise de alternativas do
            // ETP) saem de `resultados`, que só a busca preenche. Um rascunho retomado
            // pode ter sido salvo DEPOIS de uma busca de outro objeto — manter
            // `resultados` como está faria atas de objeto ERRADO vazarem como
            // alternativas (e irem ao backend se "Preencher/Sugerir" fosse clicado).
            // Limpa já e re-busca pelo OBJETO da ata (best-effort); se a re-busca
            // falhar, degrada sem alternativas automáticas — nunca mostra objeto errado.
            const objetoAlt = (ataDetalhe.objeto || '').trim()
            // Novo contexto: invalida re-buscas anteriores E carimba ESTA. A re-busca
            // é uma promise DESTACADA — sem o token, uma resposta fora de ordem (2º
            // Retomar de outro objeto, ou busca manual) sobrescreveria `resultados`
            // com atas do objeto errado (reintroduzindo o próprio sintoma do fix C).
            buscaTokenRef.current += 1
            const buscaToken = buscaTokenRef.current
            setResultados([])
            // O termo de busca prefere o salvo (o item pesquisado, ex.: "café"); só cai
            // no objeto da ata quando o rascunho não tem busca_termo. A re-busca de
            // alternativas abaixo continua usando `objetoAlt`, não `termo`.
            setTermo(buscaTermoSalvo || objetoAlt)
            setUltimaBusca(objetoAlt ? {objeto: objetoAlt, esferaGerenciador: '', uf: '', vigentes: true} : null)
            if (objetoAlt) {
                Promise.resolve()
                    .then(() => caronaApi.buscar({
                        objeto: objetoAlt,
                        esfera_aderente: null,
                        uf: null,
                        apenas_vigentes: true,
                        apenas_permitidas: false,
                        incluir_vedadas: false,
                        limite: 100,
                    }))
                    .then((data) => {
                        // Só aplica se ainda for o contexto corrente (senão é obsoleta).
                        if (buscaTokenRef.current === buscaToken) setResultados(data?.resultados || [])
                    })
                    .catch(() => {
                        if (buscaTokenRef.current === buscaToken) setResultados([])
                    })
            }
            // Nenhuma justificativa aberta: é uma entrevista em andamento.
            setJustificativa(null)
            justificativaAtualRef.current = null
            // Ciclo do rascunho: retoma NO MESMO id — o autosave passa a dar PUT nele.
            rascunhoIdRef.current = rasc.id
            // Rascunho com pesquisa vinculada (handoff feito antes de sair): rearma
            // a vigilância — o card volta e o Gerar espera a conclusão.
            setPesquisaWatchId(rasc.pesquisa_precos_id ? rasc.id : null)
            setPesquisaFluxo(null)
            pausarAutosaveRef.current = false
            criacaoRascunhoRef.current = null
            // Modo EDITÁVEL (não a CONCLUÍDA reaberta somente-leitura).
            setReaberta(false)
            setEntrevistaDisponivel(true)
            navegar('gerar')
        } catch (e: any) {
            toast.error(`Falha ao retomar o rascunho (${e?.message || 'erro'}).`)
        }
    }

    async function reabrir(id: string) {
        // Troca de registro: limpa QUALQUER espera anterior ANTES de abrir — o
        // push/timer do registro A não pode aplicar estado por cima do B.
        pendenteIaRef.current = null
        pararReconciliacaoIa()
        setIaEsperaEsgotada(false)
        // Reabrir CONCLUÍDA nunca salva rascunho: desliga o autosave e solta o id.
        rascunhoIdRef.current = null
        pausarAutosaveRef.current = true
        try {
            const j = await caronaApi.obterJustificativa(id)
            // Detalhe da ata ANTES de navegar: sem ele o andaime sairia degradado
            // ([PENDENTE] falsos, tabela vazia). Falhou? Erro honesto e o usuário
            // segue no histórico — "Abrir" de novo é o retry.
            let ataDetalhe: AtaDetalhe
            try {
                ataDetalhe = await caronaApi.detalhe(j.ata_numero_controle)
            } catch (e: any) {
                toast.error(
                    `Não foi possível carregar a ata desta justificativa (${e?.message || 'erro'}). Tente abrir novamente.`
                )
                return
            }
            const estrut = (j.documento_estruturado || {}) as any
            const respostasRegistro = (estrut.respostas || {}) as Record<string, unknown>
            // Mesmo racional do retomar: default com a esfera inferida do órgão.
            setRespostas({...defaultRespostas(esferaInferida), ...respostasRegistro})
            // Evidências REAIS do registro (o GET devolve a lista persistida) —
            // nunca zerar por cima de um histórico íntegro.
            setEvidencias(evidenciasFromBackend(j.evidencias || []))
            // Documento (ETP) pertence ao FLUXO da entrevista corrente: nunca
            // vaza para a redação de um registro reaberto do histórico.
            setDocumento(null)
            setErroDoc(null)
            setAta(ataDetalhe)
            justificativaAtualRef.current = j.id
            setJustificativa(j)
            setReaberta(true)           // regra de produto: sem auto-disparo da IA
            setEntrevistaDisponivel(Object.keys(respostasRegistro).length > 0)
            // Reaberta com a redação em andamento (ex.: outra aba disparou):
            // volta a aguardar o push + reconciliação com a MESMA margem.
            if (j.ia_texto_status === 'PROCESSANDO') aguardarIaTexto(j.id)
            navegar('justificativa')
        } catch (e: any) {
            toast.error(`Falha ao abrir a justificativa (${e?.message || 'erro'}).`)
        }
    }

    /** "Ajustar dados" de um registro REABERTO do histórico: o painel de geração
     * foi reconstruído no reabrir (respostas/evidências/ata dos dados reais do
     * registro) — carrega a lista de documentos (best-effort) e navega. */
    function voltarAjustesDoRegistro() {
        setDocumento(null)
        setErroDoc(null)
        Promise.resolve()
            .then(() => caronaApi.listarDocumentos())
            .then((ds) => setDocumentosDisponiveis(Array.isArray(ds) ? ds : []))
            .catch(() => setDocumentosDisponiveis([]))
        navegar('gerar')
    }

    function onSugestao(t: string) {
        const base = ultimaBusca || {objeto: t, esferaGerenciador: '', uf: '', vigentes: true}
        handleBuscar({...base, objeto: t})
    }

    function renderView() {
        if (view === 'busca') return <BuscaForm onBuscar={handleBuscar} onHistorico={abrirHistorico} onAbrirCaso={reabrir} onRetomarCaso={retomarRascunho} />
        if (view === 'historico') {
            return (
                <HistoricoView
                    justificativas={historico} loading={carregandoHist}
                    onAbrir={reabrir} onRetomar={retomarRascunho} onVoltar={voltar}
                />
            )
        }
        if (view === 'resultados') {
            return (
                <ResultadosList
                    resultados={resultados} origem={origem} termo={termo}
                    loading={buscando} erro={erroBusca} sugestoes={sugestoes} irps={irps}
                    sugestoesPendentes={sugestoesPendentes}
                    onRefinar={voltar} onSugestao={onSugestao}
                    onAbrir={abrirDetalhe} onAvaliar={abrirDetalhe}
                />
            )
        }
        if (view === 'detalhe') {
            return (
                <DetalheAta
                    ata={ata} resumo={resumoSel} termoBuscado={termo}
                    onVoltar={voltar}
                    onAvaliar={(selecao) => {
                        const nc = ata?.numero_controle_pncp || resumoSel?.numero_controle_pncp
                        if (nc) iniciarGeracao(nc, selecao)
                    }}
                />
            )
        }
        if (view === 'gerar' && ata) {
            return (
                <GerarJustificativa
                    ata={ata} termoBuscado={termo} aderenteNome={aderenteNome}
                    respostas={respostas} setRespostas={setRespostas}
                    gerando={gerando} onGerar={gerarJustificativa}
                    onVoltar={voltar}
                    onPesquisarPrecos={pesquisarPrecos}
                    pesquisaVinculada={pesquisaDoFluxo}
                    alternativas={alternativasAtas}
                    onAdotarAta={adotarAta}
                    onAbrirDetalhe={abrirDetalhe}
                    documento={documento} documentosDisponiveis={documentosDisponiveis}
                    enviandoDocumento={enviandoDoc} erroDocumento={erroDoc}
                    onEnviarDocumento={enviarDocumento}
                    onSelecionarDocumento={selecionarDocumento}
                    onRemoverDocumento={() => setDocumento(null)}
                />
            )
        }
        if (view === 'justificativa') {
            if (justificativa) {
                return (
                    <JustificativaView
                        justificativa={justificativa} ata={ata}
                        respostas={respostas} evidencias={evidencias} aderenteNome={aderenteNome}
                        alternativas={alternativasAtas}
                        documentoId={documento?.id || null}
                        reaberta={reaberta}
                        esperaEsgotada={iaEsperaEsgotada}
                        entrevistaIndisponivel={reaberta && !entrevistaDisponivel}
                        onAguardarIa={aguardarIaTexto}
                        onRecarregar={recarregarJustificativa}
                        onPesquisarPrecos={pesquisarPrecos}
                        onAbrirPesquisas={abrirModuloPesquisas}
                        onVoltarEntrevista={reaberta ? voltarAjustesDoRegistro : voltar}
                        onNovaBusca={irParaBusca}
                    />
                )
            }
            // Otimista: já estamos na página enquanto a justificativa é gerada.
            return (
                <div>
                    <header className="page-header">
                        <div className="crumb">
                            <a onClick={irParaBusca}>Ata Inteligente</a>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                            <a onClick={voltar}>Gerar justificativa</a>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                            <span>Gerando…</span>
                        </div>
                        <div className="header-row">
                            <h1 className="lg-h2">Justificativa de adesão</h1>
                        </div>
                    </header>
                    <div className="page-content">
                        <DicasLoading titulo="Gerando a justificativa e verificando a conformidade…" />
                    </div>
                </div>
            )
        }
        return <BuscaForm onBuscar={handleBuscar} onHistorico={abrirHistorico} onAbrirCaso={reabrir} onRetomarCaso={retomarRascunho} />
    }

    return (
        <main
            className="carona-content cscope"
            style={{'--brand-blue': COLORS.primariaEscura} as CSSProperties}
        >
            {renderView()}
        </main>
    )
}
